/**
 * HelpMenu: the "Help" row in the sidebar footer. A small menu with What's new (the latest release note, any time),
 * Send feedback and Documentation; it owns those two dialogs.
 */
import { useState } from "react";
import { Icon } from "@iconify/react";

import DropdownMenu, { DropdownEntry } from "@/components/ui/DropdownMenu";
import FeedbackDialog from "@/components/ui/FeedbackDialog";
import { useWhatsNew } from "@/hooks/useWhatsNew";
import { changelogPageUrl } from "@/services/changelog.service";
import posthog from "@/lib/posthog";
import SideTooltip from "./SideTooltip";
import { WhatsNewModal } from "./WhatsNew";

interface Props {
  collapsed?: boolean;
  /** Height class for the trigger row, matching the nav rows. */
  rowHeight?: string;
  /** Icon size in px, matching the nav icons (16 on desktop, 18 in the drawer). */
  iconSize?: number;
}

export default function HelpMenu({ collapsed = false, rowHeight = "h-9", iconSize = 16 }: Props) {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const { latest, markAllSeen } = useWhatsNew();

  const items: DropdownEntry[] = [
    latest
      ? {
          label: "What's new",
          icon: "lucide:sparkles",
          onClick: () => {
            markAllSeen();
            setWhatsNewOpen(true);
            posthog.capture("whats_new_opened", { slug: latest.slug, from: "help_menu" });
          },
        }
      : { label: "What's new", icon: "lucide:sparkles", href: changelogPageUrl, external: true },
    { label: "Send feedback", icon: "lucide:message-square", onClick: () => setFeedbackOpen(true) },
    { label: "Documentation", icon: "lucide:book-open", href: "https://docs.spidra.io/", external: true },
  ];

  const trigger = (
    <button
      type="button"
      aria-label="Help"
      className={`flex ${rowHeight} w-full cursor-pointer items-center gap-3 rounded-control text-sm text-fg-secondary transition-colors hover:bg-black/[0.04] hover:text-fg dark:hover:bg-white/[0.06] ${
        collapsed ? "justify-center px-0" : "px-3"
      }`}
    >
      <Icon icon="lucide:circle-help" width={iconSize} height={iconSize} className="shrink-0 text-fg-muted" aria-hidden="true" />
      {!collapsed && <span className="min-w-0 flex-1 truncate text-left">Help</span>}
    </button>
  );

  return (
    <>
      <SideTooltip label="Help" enabled={collapsed}>
        <DropdownMenu trigger={trigger} items={items} position="top-left" size="default" />
      </SideTooltip>
      <FeedbackDialog isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
      <WhatsNewModal entry={latest} isOpen={whatsNewOpen} onClose={() => setWhatsNewOpen(false)} />
    </>
  );
}
